import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { apiService } from '../services';
import { Ranking as RankingType, RankingItem } from '../types/ranking';
import {
  WorkoutResult,
  AMRAPResult,
  ForTimeResult,
  EMOMResult,
  MaxTimeResult,
  MaxWeightResult
} from '../types/result';

interface Event {
  id: string;
  name: string;
}

const PublicRanking: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [event, setEvent] = useState<Event | null>(null);
  const [rankings, setRankings] = useState<RankingType[]>([]);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedGender, setSelectedGender] = useState<string>(searchParams.get('gender') || '');
  const [selectedCategory, setSelectedCategory] = useState<string>(searchParams.get('category') || '');
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const tabsContainerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (eventId) {
      loadEventAndRankings();
    }
  }, [eventId, selectedGender, selectedCategory]);

  useEffect(() => {
    checkScrollPosition();
    window.addEventListener('resize', checkScrollPosition);
    return () => window.removeEventListener('resize', checkScrollPosition);
  }, [rankings]);

  const checkScrollPosition = () => {
    const container = tabsContainerRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setShowLeftScroll(scrollLeft > 10);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const handleScroll = () => {
    checkScrollPosition();
  };

  const handleGenderChange = (gender: string) => {
    setSelectedGender(gender);
    const newParams = new URLSearchParams(searchParams);
    if (gender) {
      newParams.set('gender', gender);
    } else {
      newParams.delete('gender');
    }
    setSearchParams(newParams);
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    const newParams = new URLSearchParams(searchParams);
    if (category) {
      newParams.set('category', category);
    } else {
      newParams.delete('category');
    }
    setSearchParams(newParams);
  };

  const loadEventAndRankings = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query params
      const params = new URLSearchParams();
      if (selectedGender) params.append('gender', selectedGender);
      if (selectedCategory) params.append('category', selectedCategory);
      const queryString = params.toString() ? `?${params.toString()}` : '';

      const [eventData, rankingsData] = await Promise.all([
        apiService.get<Event>(`/events/${eventId}`),
        apiService.get<RankingType[]>(`/events/${eventId}/rankings${queryString}`)
      ]);
      setEvent(eventData);
      setRankings(rankingsData);
      setActiveTab(0);
    } catch (err: any) {
      setError(err.message || 'Failed to load rankings');
    } finally {
      setLoading(false);
    }
  };

  const getMedalIcon = (position: number) => {
    switch (position) {
      case 1:
        return <span className="text-xl sm:text-2xl">🥇</span>;
      case 2:
        return <span className="text-xl sm:text-2xl">🥈</span>;
      case 3:
        return <span className="text-xl sm:text-2xl">🥉</span>;
      default:
        return null;
    }
  };

  const getPositionClass = (position: number) => {
    switch (position) {
      case 1:
        return 'bg-white/5 backdrop-blur-sm text-white border-l-4 border-yellow-400';
      case 2:
        return 'bg-white/5 backdrop-blur-sm text-white border-l-4 border-gray-400';
      case 3:
        return 'bg-white/5 backdrop-blur-sm text-white border-l-4 border-amber-500';
      default:
        return 'bg-white/5 backdrop-blur-sm text-white border-l-2 border-gray-600';
    }
  };

  const formatWorkoutResult = (result: WorkoutResult | undefined): string => {
    if (!result) return 'No result';

    switch (result.type) {
      case 'AMRAP': {
        const amrapResult = result as AMRAPResult;
        return `${amrapResult.reps} reps`;
      }
      case 'FOR_TIME': {
        const forTimeResult = result as ForTimeResult;
        if (forTimeResult.cap) {
          const reps = forTimeResult.reps || 0;
          return `CAP+${reps}`;
        } else {
          const mins = forTimeResult.mins || 0;
          const secs = forTimeResult.seconds || 0;
          return `${mins}:${secs.toString().padStart(2, '0')}`;
        }
      }
      case 'EMOM': {
        const emomResult = result as EMOMResult;
        const lastReps = emomResult.lastRoundReps ? `+${emomResult.lastRoundReps}` : '';
        return `${emomResult.roundsCompleted} rounds${lastReps}`;
      }
      case 'MAX_TIME': {
        const maxTimeResult = result as MaxTimeResult;
        return `${maxTimeResult.mins}:${maxTimeResult.seconds.toString().padStart(2, '0')}`;
      }
      case 'MAX_WEIGHT': {
        const maxWeightResult = result as MaxWeightResult;
        return `${maxWeightResult.weight} lbs`;
      }
      default:
        return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-gray-950 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-16 w-16 text-white mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-400 text-lg">Loading rankings...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-gray-950 to-slate-950 flex items-center justify-center">
        <div className="text-center max-w-md mx-4">
          <svg className="h-16 w-16 text-red-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-4 text-xl font-semibold text-white">Error Loading Rankings</h3>
          <p className="mt-2 text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!event || rankings.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-gray-950 to-slate-950 flex items-center justify-center">
        <div className="text-center max-w-md mx-4">
          <svg className="h-16 w-16 text-gray-600 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h3 className="mt-4 text-xl font-semibold text-white">No Rankings Available</h3>
          <p className="mt-2 text-gray-500">No rankings found for this event</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-gray-950 to-slate-950 py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-4 sm:mb-8 text-center">
          <div className="inline-flex items-center justify-center mb-2 sm:mb-4 bg-white/10 backdrop-blur-sm p-2 sm:p-3 rounded-full border border-white/20">
            <svg className="h-8 w-8 sm:h-12 sm:w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black text-white tracking-tight uppercase px-2 leading-tight break-words">{event.name}</h1>
          <div className="mt-2 sm:mt-3 inline-block">
            <p className="text-sm sm:text-lg md:text-xl lg:text-2xl text-gray-400 font-semibold uppercase tracking-wide sm:tracking-widest">
              Live Rankings
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row justify-center gap-2 sm:gap-4">
          <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg px-3 sm:px-4 py-2">
            <select
              value={selectedGender}
              onChange={(e) => handleGenderChange(e.target.value)}
              className="bg-transparent text-white font-semibold uppercase tracking-wide text-xs sm:text-sm focus:outline-none cursor-pointer w-full sm:w-auto"
            >
              <option value="" className="bg-slate-900">All Genders</option>
              <option value="MALE" className="bg-slate-900">Male</option>
              <option value="FEMALE" className="bg-slate-900">Female</option>
            </select>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg px-3 sm:px-4 py-2">
            <select
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="bg-transparent text-white font-semibold uppercase tracking-wide text-xs sm:text-sm focus:outline-none cursor-pointer w-full sm:w-auto"
            >
              <option value="" className="bg-slate-900">All Categories</option>
              <option value="RX" className="bg-slate-900">RX</option>
              <option value="SCALED" className="bg-slate-900">Scaled</option>
            </select>
          </div>
        </div>

        {/* Tabs */}
        <div className="relative border-b border-white/10 mb-6 sm:mb-8 -mx-3 sm:mx-0">
          {/* Left scroll indicator */}
          {showLeftScroll && (
            <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-slate-950 to-transparent z-10 pointer-events-none flex items-center justify-start pl-2">
              <svg className="h-6 w-6 text-white/60 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </div>
          )}

          {/* Right scroll indicator */}
          {showRightScroll && (
            <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-slate-950 to-transparent z-10 pointer-events-none flex items-center justify-end pr-2">
              <svg className="h-6 w-6 text-white/60 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          )}

          <nav
            ref={tabsContainerRef}
            onScroll={handleScroll}
            className="flex space-x-1 sm:space-x-2 overflow-x-auto px-3 sm:px-0 pb-0 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent scroll-smooth"
          >
            {rankings.map((ranking, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={`px-4 sm:px-6 md:px-8 py-3 sm:py-4 font-bold text-xs sm:text-sm md:text-base whitespace-nowrap border-b-2 transition-all uppercase tracking-wide flex-shrink-0 ${
                  activeTab === index
                    ? 'border-white text-white bg-white/5'
                    : 'border-transparent text-gray-500 hover:text-gray-300 hover:bg-white/5'
                }`}
              >
                {ranking.name}
                {ranking.name === 'General' && (
                  <svg className="inline-block h-4 w-4 sm:h-5 sm:w-5 ml-1 sm:ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Ranking Content */}
        {rankings[activeTab] && (
          <div className="bg-white/5 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden border border-white/10">
            <div className="bg-white/5 backdrop-blur-sm px-3 sm:px-6 md:px-8 py-4 sm:py-6 border-b border-white/10">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black text-white text-center uppercase tracking-wide sm:tracking-wider break-words">
                {rankings[activeTab].name}
              </h2>
            </div>

            <div className="divide-y divide-white/10">
              {rankings[activeTab].results.map((item, index) => (
                <div
                  key={index}
                  className={`px-2 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4 transition-all hover:scale-[1.01] ${getPositionClass(item.position)}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4 flex-1 min-w-0">
                      {/* Position */}
                      <div className="flex items-center justify-center w-8 sm:w-10 md:w-12 lg:w-16 flex-shrink-0">
                        {getMedalIcon(item.position) || (
                          <span className="text-base sm:text-lg md:text-xl lg:text-2xl font-black text-white">#{item.position}</span>
                        )}
                      </div>

                      {/* Name */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-black text-white uppercase tracking-wide break-words">{item.name}</h3>

                        {/* Result (for workout rankings) */}
                        {item.result && (
                          <div className="mt-0.5 sm:mt-1">
                            <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-xs sm:text-sm md:text-base font-bold bg-white/10 text-white border border-white/20">
                              {formatWorkoutResult(item.result)}
                            </span>
                          </div>
                        )}

                        {/* Workout Results (for General ranking) */}
                        {item.workoutResults.length > 0 && (
                          <div className="mt-1 sm:mt-1.5 flex flex-wrap gap-1 sm:gap-1.5">
                            {item.workoutResults.map((workoutResult, idx) => (
                              <div
                                key={idx}
                                className="inline-flex items-center px-1.5 sm:px-2 py-0.5 bg-white/10 border border-white/20 rounded text-[10px] sm:text-xs text-gray-300 font-semibold"
                              >
                                <span className="font-bold text-white">{workoutResult.name}:</span>
                                <span className="ml-0.5 sm:ml-1">#{workoutResult.position} ({workoutResult.score.toFixed(1)} pts)</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Tiebreaker Breakdown */}
                        {item.tiebreakerBreakdown && item.tiebreakerBreakdown.length > 0 && (
                          <div className="mt-1 sm:mt-1.5">
                            <div className="flex items-start gap-1">
                              <svg className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-yellow-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                              <div className="flex flex-col gap-0.5">
                                {item.tiebreakerBreakdown.map((breakdownItem, idx) => (
                                  <p key={idx} className="text-[9px] sm:text-[10px] text-gray-400 italic leading-tight">
                                    {breakdownItem}
                                  </p>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Score */}
                      <div className="text-right bg-white/5 backdrop-blur-sm px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 md:py-2.5 rounded-lg border border-white/10 flex-shrink-0">
                        <div className="text-[8px] sm:text-[10px] md:text-xs text-gray-400 font-bold uppercase tracking-wider">
                          {rankings[activeTab].name === 'General' ? 'Total' : 'Points'}
                        </div>
                        <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-black text-white">
                          {rankings[activeTab].name === 'General' ? item.score.toFixed(1) : item.score.toFixed(0)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 sm:mt-10 text-center">
          <div className="inline-block px-4 sm:px-6 py-2 sm:py-3 bg-white/5 backdrop-blur-sm rounded-full border border-white/10">
            <p className="text-gray-500 text-xs sm:text-sm font-semibold uppercase tracking-wide sm:tracking-widest">
              Powered by CrossFit Events Management
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicRanking;
